import { useSelector } from "react-redux";
import ExitSurveyModal from "./ExitSurveyModal";
import { getApplicationRoleType, getCurrentUserId, getLoanId, getLoanPurpose, getNhApplicationPath, getNhDealApplicationId, getUserRole } from "features/authSlice";
import { useEffect, useMemo, useState } from "react";
import { useExitIntentHandler } from "hooks/useExitIntentHandler";
import { useNavigate } from "react-router-dom";
import {
  CreateSurveyDto,
  Feedback,
  Survey,
  SurveyType,
  useCreateSurveyMutation,
  useGetSurveyDetailsQuery,
  useUpdateSurveyDisplayedCountMutation,
  useUpdateSurveyMutation,
} from "../surveyService";
import { EXIT_SURVEY_LIMIT } from "utilities/Constant";
import { useGetloanOfficerQuery } from "pages/application/applicationService";
import { getLoanOfficerDomainName } from "services/common/loanOfficerSlice";
import { trackFSExitIntentCancelled, trackFSExitIntentCompleted, trackFSExitIntentDisplayed } from "utilities/full-story";

export default function ExitSurvey({ stepCode, stepCodeName }: { stepCode: string; stepCodeName?: string }) {
  const navigate = useNavigate();
  const loanId = useSelector(getLoanId);
  const exitSurveySessionkey = `exitSurveyDisplayed_${loanId}`;
  const [showExitSurvey, setShowExitSurvey] = useState(false);
  const exitSurveyDisplayedForSession = sessionStorage.getItem(exitSurveySessionkey) === "true";
  const userRole = useSelector(getUserRole);
  const path = useSelector(getNhApplicationPath);
  const applicationRoleType = useSelector(getApplicationRoleType);
  const userId = useSelector(getCurrentUserId);
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const loanPurpose = useSelector(getLoanPurpose);
  const loanOfficerDomainName = useSelector(getLoanOfficerDomainName);
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [createSurvey, { isLoading }] = useCreateSurveyMutation();
  const [updateSurvey, { isLoading: isSurveySubmitted }] = useUpdateSurveyMutation();
  const [updateSurveyDisplayedCount, { isLoading: isSurveyDisplayedCountUpdated }] = useUpdateSurveyDisplayedCountMutation();
  const { data: surveyDetails, isSuccess: isSurveyDetailsSuccess } = useGetSurveyDetailsQuery({
    userId,
    nhDealApplicationId,
    surveyType: SurveyType.EXIT,
  });
  const { data: loanOfficerDetails } = useGetloanOfficerQuery(nhDealApplicationId, { skip: !nhDealApplicationId });

  const salesChannel = useMemo(() => {
    const domainName = loanOfficerDomainName || (loanOfficerDetails?.domains?.[0]?.domainName ?? "").trim().toLowerCase();
    if (domainName === "retail") return "Retail";
    if (domainName === "cd") return "CD";
    return undefined;
  }, [loanOfficerDomainName, loanOfficerDetails]);

  const getBorrowerType = useMemo(() => {
    if (applicationRoleType?.toLowerCase() === "primaryborrower") {
      return userRole?.toLowerCase() === "borrower" ? "primary" : "spouse";
    } else if (applicationRoleType?.toLowerCase() === "nonspousalcoborrower") {
      return userRole?.toLowerCase() === "borrower" ? "nscb" : "spouse";
    }
    return "unknown";
  }, [applicationRoleType, userRole]);

  const isSurveyDisplayableStepCode = useMemo(() => {
    switch (getBorrowerType) {
      case "primary":
        return !["B4", "B1"].includes(stepCode);

      case "spouse":
        return stepCode !== "C10";

      case "nscb":
        return (path?.toLowerCase() === "fullpath" && stepCode !== "C16") || (path?.toLowerCase() !== "fullpath" && stepCode !== "C17");

      default:
        return false;
    }
  }, [getBorrowerType, stepCode, path]);

  const isSurveyDisplayable = useMemo(() => {
    return (
      isSurveyDetailsSuccess &&
      (!survey || survey?.displayedCount < EXIT_SURVEY_LIMIT) &&
      isSurveyDisplayableStepCode &&
      !exitSurveyDisplayedForSession
    );
  }, [isSurveyDetailsSuccess, survey, isSurveyDisplayableStepCode, exitSurveyDisplayedForSession]);

  const { exitIntent, triggerPoint, nextUrl } = useExitIntentHandler(exitSurveySessionkey, isSurveyDisplayable);

  const setSurveyDisplayedForSession = () => {
    sessionStorage.setItem(exitSurveySessionkey, "true");
  };

  const submitExitSurvey = (feedback: Feedback[]) => {
    trackFSExitIntentCompleted({
      loanPurpose,
      salesChannel,
      stepCode,
      stepCodeName,
      question1: feedback[0]?.answer,
      question2: feedback[1]?.answer,
    });
    void triggerSubmitSurvey(feedback);
    setSurveyDisplayedForSession();
    setShowExitSurvey(false);
  };

  const closeExitSurvey = (partialFeedback: Feedback[]) => {
    trackFSExitIntentCancelled({
      loanPurpose,
      salesChannel,
      stepCode,
      stepCodeName,
      question1: partialFeedback[0]?.answer,
      question2: partialFeedback[1]?.answer,
    });
    void triggerUpdateSurveyDisplayedCount();
    setSurveyDisplayedForSession();
    setShowExitSurvey(false);
  };

  const triggerCreateSurvey = async () => {
    const surveyData: CreateSurveyDto = {
      type: SurveyType.EXIT,
      triggerPoint: triggerPoint!,
      Device: navigator.userAgent || "Unknown",
    };

    await createSurvey({ userId, nhDealApplicationId, surveyData });
  };

  const triggerUpdateSurveyDisplayedCount = async () => {
    if (!survey?.id) return;
    await updateSurveyDisplayedCount({ surveyId: survey.id });
  };

  const triggerSubmitSurvey = async (feedback: Feedback[]) => {
    if (!survey?.id) return;
    await updateSurvey({ surveyId: survey.id, surveyData: { ...survey, feedbacks: feedback } });
  };

  useEffect(() => {
    if (surveyDetails) setSurvey(surveyDetails);
  }, [surveyDetails]);

  useEffect(() => {
    if (isSurveyDisplayable && exitIntent) {
      if (!survey) void triggerCreateSurvey();
      trackFSExitIntentDisplayed({
        loanPurpose,
        salesChannel,
        stepCode,
        stepCodeName,
      });
      setShowExitSurvey(true);
    }
  }, [isSurveyDisplayable, exitIntent]);

  useEffect(() => {
    if ((isSurveySubmitted || isSurveyDisplayedCountUpdated) && nextUrl) {
      navigate(nextUrl, { replace: true, state: { previousPath: location.pathname } });
    }
  }, [isSurveySubmitted, isSurveyDisplayedCountUpdated]);

  return showExitSurvey ? <ExitSurveyModal closeSurvey={closeExitSurvey} submitSurvey={submitExitSurvey} isLoading={isLoading} /> : null;
}
